import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { listAppClients, setClientTargets, backfillCoachThreads, backfillAppInvites, getAppLinks, putAppLinks, deleteAppAccount } from "@/lib/api";
import OnboardingDialog from "@/components/OnboardingDialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Smartphone, MessageSquare, Target, RefreshCw, AlertTriangle, Mail, Link2, Trash2 } from "lucide-react";

const TARGET_INPUTS = [
  { key: "kcal", label: "Calories", unit: "kcal" },
  { key: "protein_g", label: "Protein", unit: "g" },
  { key: "carbs_g", label: "Carbs", unit: "g" },
  { key: "fat_g", label: "Fat", unit: "g" },
  { key: "fibre_g", label: "Fibre", unit: "g" },
  { key: "water_ml", label: "Water", unit: "ml" },
  { key: "steps", label: "Steps", unit: "/day" },
  { key: "sleep_h", label: "Sleep", unit: "h" },
  { key: "sessions_per_week", label: "Gym sessions", unit: "/week" },
];

const TARGET_STATUS = {
  confirmed: { label: "Confirmed", cls: "text-emerald-300 bg-emerald-500/10" },
  suggested: { label: "To confirm", cls: "text-amber-300 bg-amber-500/10" },
  needs_coach: { label: "Coach to set (medical)", cls: "text-rose-300 bg-rose-500/10" },
};

export default function MemberAppClients() {
  const { activeFacility, effectiveRole, user } = useAuth();
  const isManager = atLeast(effectiveRole || user?.role, "manager");
  const isMaster = atLeast(effectiveRole || user?.role, "master_admin");
  const [clients, setClients] = useState([]);
  const [coachFlags, setCoachFlags] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [linksOpen, setLinksOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listAppClients();
      setClients(r.clients || []);
      setCoachFlags(r.coach_flags || []);
    } catch {
      toast.error("Could not load app clients");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => `${c.name || ""} ${c.phone || ""} ${c.thread?.coach_name || ""}`.toLowerCase().includes(q));
  }, [clients, filter]);

  const counts = useMemo(() => ({
    total: clients.length,
    joined: clients.filter((c) => c.invite_status === "joined").length,
    oriented: clients.filter((c) => c.orientation_done).length,
    onboarded: clients.filter((c) => c.onboarding?.applies && c.onboarding.complete).length,
    toConfirm: clients.filter((c) => c.targets_status === "suggested" || c.targets_status === "needs_coach").length,
  }), [clients]);

  const backfill = async () => {
    setBackfilling(true);
    try {
      const r = await backfillCoachThreads();
      toast.success(`Created ${r.created} coach thread${r.created === 1 ? "" : "s"}` +
        (r.skipped_no_phone ? ` · ${r.skipped_no_phone} skipped (no usable phone)` : ""));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create threads");
    } finally {
      setBackfilling(false);
    }
  };

  const inviteAll = async () => {
    setInviting(true);
    try {
      const r = await backfillAppInvites();
      toast.success(r.queued ? `${r.queued} invite${r.queued === 1 ? "" : "s"} queued, ${r.sent} sent` +
        (r.mode === "off" ? " (sending is off: they'll go once it's switched on)" : "") : "Everyone sold into a challenge already has an invite");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invites");
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="app-clients-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Member App · Coaching</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">App Clients</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Members set up for the LIFTR app at {activeFacility?.name || "this gym"}. 6 Week Challenge clients appear here as soon as
            their consult is saved as sold, and get an email to download the app; everyone else appears once they sign in.
          </p>
        </div>
        <div className="flex gap-2">
          {isMaster && (
            <Button variant="outline" onClick={() => setLinksOpen(true)} data-testid="app-links" title="Download links used in the invite email">
              <Link2 className="mr-1.5 h-4 w-4" /> App links
            </Button>
          )}
          {isManager && (
            <Button variant="outline" onClick={inviteAll} disabled={inviting} data-testid="invite-sold"
              title="Email the download link to challenge clients sold before invites existed (each consult is invited once)">
              <Mail className="mr-1.5 h-4 w-4" /> {inviting ? "Sending…" : "Invite sold clients"}
            </Button>
          )}
          {isManager && (
            <Button variant="outline" onClick={backfill} disabled={backfilling} data-testid="backfill-threads"
              title="Create coach threads for challenge clients who started before the app existed">
              <MessageSquare className="mr-1.5 h-4 w-4" /> {backfilling ? "Creating…" : "Add running challenges"}
            </Button>
          )}
          <Button variant="outline" onClick={load} data-testid="refresh-app-clients"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {coachFlags.length > 0 && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.06] p-4" data-testid="coach-flags">
          <div className="flex items-center gap-2 text-sm font-semibold text-rose-200">
            <AlertTriangle className="h-4 w-4" /> {coachFlags.length} challenge client{coachFlags.length === 1 ? "" : "s"} without a coach after 24 hours
          </div>
          <p className="mt-1 text-xs text-zinc-400">Assign an accountability coach on the consult. The coach chat opens only once a coach is set.</p>
          <ul className="mt-2 space-y-1 text-sm">
            {coachFlags.map((f) => (
              <li key={f.lead_id} className="flex flex-wrap items-center gap-2 text-zinc-200">
                <span className="font-medium">{f.name}</span>
                <span className="text-xs text-zinc-500">sold {f.hours} h ago{f.start_date ? ` · starts ${fmtDay(f.start_date)}` : ""}</span>
                {f.urgent && <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-rose-200">Started without a coach</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="app-clients-kpis">
        <Kpi label="App clients" value={counts.total} />
        <Kpi label="Signed in" value={counts.joined} tint="emerald" />
        <Kpi label="Onboarding done" value={counts.onboarded} />
        <Kpi label="Targets to confirm" value={counts.toConfirm} tint={counts.toConfirm ? "amber" : undefined} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name, phone or coach…"
          className="max-w-sm" data-testid="filter-app-clients" />
        <span className="text-xs text-zinc-500">{filtered.length} clients</span>
      </div>

      {loading ? (
        <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-zinc-500" data-testid="app-clients-empty">
          <Smartphone className="mx-auto mb-3 h-6 w-6 text-zinc-600" />
          No app clients yet. They appear when a 6 Week Challenge starts, or when a member signs in to the app.
        </div>
      ) : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="app-clients-table">
            <thead>
              <tr className="border-b border-[#1f1f1f] text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Coach</th>
                <th className="px-4 py-3">App</th>
                <th className="px-4 py-3">Onboarding</th>
                <th className="px-4 py-3">Targets</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const st = TARGET_STATUS[c.targets_status];
                return (
                  <tr key={c.id} className="border-b border-[#161616] last:border-0" data-testid={`app-client-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-100">{c.name || "—"}</div>
                      <div className="text-[11px] text-zinc-500">+{c.phone}{c.thread?.programme ? ` · ${c.thread.programme}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">
                      {c.thread?.coach_name || <span className="text-zinc-600">—</span>}
                      {c.thread?.status === "ended" && <div className="text-[10px] text-zinc-500">programme ended</div>}
                    </td>
                    <td className="px-4 py-3">
                      {c.invite_status === "joined" ? (
                        <span className="text-emerald-300">Signed in</span>
                      ) : (
                        <span className="text-zinc-500">Not yet</span>
                      )}
                      {c.invite_status !== "joined" && c.invite && (
                        <div className={`text-[10px] ${INVITE[c.invite.status]?.cls || "text-zinc-500"}`}>{INVITE[c.invite.status]?.label || c.invite.status}</div>
                      )}
                      {c.last_login_at && <div className="text-[10px] text-zinc-500">last {fmtDate(c.last_login_at)}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {!c.onboarding?.applies ? <span className="text-zinc-600">—</span> : (
                      <button type="button" onClick={() => setViewing(c)} className="text-left hover:underline" data-testid={`onboarding-${c.id}`}>
                        {c.onboarding?.complete ? <span className="text-emerald-300">Done</span>
                          : <span className="text-zinc-300">{c.onboarding?.done ?? 0} of {c.onboarding?.total ?? 9}</span>}
                        {c.onboarding?.test_best && !c.onboarding.complete && (
                          <span className="ml-1 text-[11px] text-zinc-500">· test {c.onboarding.test_best.score}/{c.onboarding.test_best.of}</span>
                        )}
                      </button>
                      )}
                      {c.photo_note_to_review && <div className="mt-0.5 text-[10px] text-sky-300">Photo note to review</div>}
                      {c.parq?.any_yes && (
                        <div className={`mt-0.5 text-[10px] ${c.parq.cleared ? "text-zinc-500" : "text-amber-300"}`}>
                          Health check: {c.parq.areas?.length ? c.parq.areas.join(", ").replace(/_/g, " ") : "yes answers"}{c.parq.cleared ? " · cleared" : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {st ? (
                        <span className={`rounded-full px-2 py-0.5 text-[11px] ${st.cls}`}>{st.label}</span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                      {c.targets?.kcal && <div className="mt-1 text-[11px] text-zinc-500">{c.targets.kcal} kcal · {c.targets.protein_g} g protein</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant={c.targets_status === "confirmed" ? "outline" : "default"}
                        onClick={() => setEditing(c)} disabled={!c.targets && !c.orientation_done}
                        title={!c.targets && !c.orientation_done ? "Available once the client finishes orientation in the app" : ""}
                        data-testid={`edit-targets-${c.id}`}>
                        <Target className="mr-1 h-3.5 w-3.5" />
                        {c.targets_status === "confirmed" ? "Edit targets" : "Review targets"}
                      </Button>
                      {isMaster && (
                        <Button size="sm" variant="ghost" className="ml-1 text-zinc-500 hover:text-rose-400" onClick={() => setDeleting(c)}
                          title="Delete app account (for deletion requests by email)" aria-label={`Delete ${c.name}'s app account`}
                          data-testid={`delete-account-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {deleting && <DeleteAccountDialog client={deleting} onClose={(done) => { setDeleting(null); if (done) load(); }} />}
      {viewing && <OnboardingDialog client={viewing} canManage={isManager} onClose={() => { setViewing(null); load(); }} />}
      {linksOpen && <LinksDialog onClose={() => setLinksOpen(false)} />}

      {editing && (
        <TargetsDialog client={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function TargetsDialog({ client, onClose, onSaved }) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(TARGET_INPUTS.map((t) => [t.key, client.targets?.[t.key] ?? ""])));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const payload = {};
    for (const t of TARGET_INPUTS) {
      const v = String(values[t.key]).trim();
      if (v !== "") payload[t.key] = Number(v);
    }
    setSaving(true);
    try {
      await setClientTargets(client.id, payload);
      toast.success(`Targets confirmed for ${client.name}`);
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Check the values — one is out of range");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg" data-testid="targets-dialog">
        <DialogHeader>
          <DialogTitle>Daily targets · {client.name}</DialogTitle>
          <DialogDescription>
            {client.targets_status === "needs_coach"
              ? "This client flagged a medical condition, so set their targets yourself."
              : "Suggested from their orientation. Adjust anything, then confirm — the client sees these in the app."}
            {client.goal ? ` Goal: ${client.goal.replace("_", " ")}.` : ""}{client.diet_pref ? ` Diet: ${client.diet_pref.replace("_", "-")}.` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          {TARGET_INPUTS.map((t) => (
            <label key={t.key} className="block">
              <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">{t.label} <span className="normal-case tracking-normal">({t.unit})</span></div>
              <Input type="number" value={values[t.key]} data-testid={`target-${t.key}`}
                onChange={(e) => setValues((v) => ({ ...v, [t.key]: e.target.value }))} />
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="targets-save">{saving ? "Saving…" : "Confirm targets"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinksDialog({ onClose }) {
  const [links, setLinks] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getAppLinks().then(setLinks, () => toast.error("Could not load the links")); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await putAppLinks({ android_url: links.android_url || null, ios_url: links.ios_url || null });
      toast.success("Download links saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the links");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg" data-testid="links-dialog">
        <DialogHeader>
          <DialogTitle>App download links</DialogTitle>
          <DialogDescription>
            Used in the &quot;get the LIFTR app&quot; email sent when a 6 Week Challenge consult is sold. Leave iPhone empty until the
            App Store listing is live: the email then says the iPhone app is coming soon. Invites are {links?.invites === "ghl" ? "switched on" : links?.invites === "console" ? "in test mode (logged, not sent)" : "switched off (APP_INVITES)"}.
          </DialogDescription>
        </DialogHeader>
        {links ? (
          <div className="space-y-3">
            {[["android_url", "Android (Google Play)"], ["ios_url", "iPhone (App Store or TestFlight)"]].map(([k, label]) => (
              <label key={k} className="block">
                <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
                <Input value={links[k] || ""} placeholder="https://…" onChange={(e) => setLinks({ ...links, [k]: e.target.value })} data-testid={`link-${k}`} />
              </label>
            ))}
          </div>
        ) : <div className="text-sm text-zinc-500">Loading…</div>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !links} data-testid="links-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const INVITE = {
  sent: { label: "Invite emailed", cls: "text-emerald-400/80" },
  logged: { label: "Invite logged (test mode)", cls: "text-zinc-500" },
  queued: { label: "Invite queued", cls: "text-zinc-500" },
  off: { label: "Invites switched off", cls: "text-zinc-500" },
  no_email: { label: "No email on the consult", cls: "text-amber-300" },
  no_links: { label: "Invite waiting for app links", cls: "text-amber-300" },
  no_webhook: { label: "Invite: GoHighLevel not set up", cls: "text-amber-300" },
  failed: { label: "Invite failed", cls: "text-rose-300" },
};

const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

function Kpi({ label, value, tint }) {
  const tintCls = tint === "emerald" ? "text-emerald-300" : tint === "amber" ? "text-amber-300" : "text-zinc-100";
  return (
    <div className="card-surface p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`font-display mt-1 text-xl font-extrabold tabular-nums ${tintCls}`}>{value}</div>
    </div>
  );
}

const fmtDate = (isoStr) => new Date(isoStr).toLocaleDateString(undefined, { day: "numeric", month: "short" });


// For deletion requests that arrive by email (liftr.fit/delete-account). Same as the member's own
// "Delete my account": app data goes, the membership, payments and check-ins stay.
function DeleteAccountDialog({ client, onClose }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = typed.trim().toUpperCase() === "DELETE";
  const remove = async () => {
    setBusy(true);
    try {
      await deleteAppAccount(client.id);
      toast.success(`${client.name}'s app account was deleted`);
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete the account");
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
        <DialogHeader>
          <DialogTitle>Delete {client.name}'s app account?</DialogTitle>
          <DialogDescription>
            Only do this when the member asked for it (by email from the address on file). It can't be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm text-zinc-400">
          <p><span className="text-zinc-200">Deleted:</span> their app account, coach chat and voice notes, photos, meals,
            body checks, workouts, health data, XP and app answers. They're signed out everywhere.</p>
          <p><span className="text-zinc-200">Kept:</span> membership, payments, check-ins, pauses and reward points. Their
            membership isn't cancelled.</p>
        </div>
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type DELETE to confirm"
          data-testid="delete-account-confirm" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button className="bg-rose-600 hover:bg-rose-500" disabled={!ready || busy} onClick={remove}
            data-testid="delete-account-submit">
            {busy ? "Deleting…" : "Delete app account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
